import type {TMinorActionPromptDataSlyDeal} from '@kapitalist/contract/types'
import {skins} from '~/game/skins'
import Card from '~/models/game/Card'
import {propertyCardFactory} from '~/models/game/factories'
import type GamePlayer from '~/models/game/GamePlayer'
import {PlayerGamePosition} from '~/models/game/GamePosition'
import type {
  TMinorActionPromptUserFacingDescriptionData,
  TMinorActionPromptValidResponseData,
} from '~/models/game/MinorActionPrompt'
import MinorActionPrompt from '~/models/game/MinorActionPrompt'
import MinorActionAcceptSlyDeal from '~/models/game/MinorActions/MinorActionAcceptSlyDeal'
import type {AnyMinorAction, AnyPropertyCard} from '~/models/game/types'
import {mutateIf} from '~/utils/etc'
import {sentenceCase, xVerbY} from '~/utils/language'

export default class MinorActionPromptSlyDeal extends MinorActionPrompt<TMinorActionPromptDataSlyDeal> {
  requestedCard: AnyPropertyCard

  constructor(
    data: TMinorActionPromptDataSlyDeal,
    majorActor: GamePlayer,
    minorActor: GamePlayer
  ) {
    super(data, majorActor, minorActor)
    this.requestedCard = propertyCardFactory(data.requestedCard)
  }

  protected _validResponses(
    data: TMinorActionPromptValidResponseData
  ): AnyMinorAction | AnyMinorAction[] {
    return this.withJustSayNo(
      data,
      new MinorActionAcceptSlyDeal({
        minorActorPlayerId: this.minorActorPlayerId,
        type: 'slyDeal',
        requestedCard: this.requestedCard.datasource,
      })
    )
  }

  public userFacingDescription(
    data: TMinorActionPromptUserFacingDescriptionData
  ): string {
    const {gamePosition, tense, skin} = data
    const currentPlayer =
      gamePosition instanceof PlayerGamePosition
        ? gamePosition.currentPlayer
        : undefined
    const {majorActor, minorActor} = this

    const {conjugatedVerb, xNoun, yNounPossessive} = xVerbY(
      tense,
      minorActor,
      'respondTo',
      majorActor,
      currentPlayer?.id
    )

    const dropFirstNoun = tense === 'imperativePrompt'
    return skins[skin].gameLogDescriptions.minorActionPrompts.slyDeal({
      tense,
      actor: dropFirstNoun
        ? undefined
        : xNoun === minorActor.username
          ? xNoun
          : sentenceCase(xNoun),
      verb: mutateIf(conjugatedVerb, dropFirstNoun, sentenceCase),
      targetPossessive: yNounPossessive,
      cardName: Card.displayNameForCard('slyDeal', skin),
    })
  }
}
